import { navigate } from "gatsby";
import { useSnackbar } from "notistack";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
  useInfiniteQuery,
} from "react-query";
import { useMemo, useEffect } from "react";
import {
  companyApproval,
  createHomePageDetails,
  extractCompanyDomain,
  getComapnyStatictics,
  getCompanyConnectiveDetails,
  getCompanyInfo,
  getCompanyList,
  toggleCompanyStatus,
  updateCompany,
  updateCompanyDuringSignup,
  updateHomePageDetails,
} from "../api-services/company";
import { getSendingStatus } from "../api-services/email";
import { QUERY_CACHE_TIME, STAFF_ROLE, USER_ROLE } from "../commons/enum";
import { getcompanyDomain } from "../utils/get-params-value";
import { useMyInfo } from "./user.hooks";
import { applyTenantTheme } from "../utils/theme-utils";
import { isBrowser } from "../utils/auth";
import { setCompanyIdCookie } from "../utils/company-cookie";
import { QUERY_KEY } from "../commons/query-key";
import { useMyStaffInfo } from "./staff.hooks";
import { generateCompanyInvoices } from "../api-services/billing";

const changeFavicon = (iconUrl) => {
  // Remove all existing favicons
  document
    .querySelectorAll("link[rel='icon']")
    .forEach((icon) => icon.remove());
  // Add new favicon
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = iconUrl;
  document.head.appendChild(link);
};

export const useCompanyList = (query) => {
  const { data: userInfo } = useMyInfo() as UseQueryResult<any, unknown>;
  const { data: staffInfo } = useMyStaffInfo() as UseQueryResult<any, unknown>;
  return useQuery(
    [QUERY_KEY.COMPANY_LIST, query],
    () => getCompanyList(query),
    {
      enabled:
        userInfo?.userRole === USER_ROLE.ADMIN ||
        staffInfo?.staffRoles.some(
          (role) =>
            role.role === STAFF_ROLE.COMPANY_ADMIN ||
            role.role === STAFF_ROLE.SUPPORT
        ),
    }
  );
};

export const useCompanyListStatictics = (companyIds) => {
  return useQueries(
    companyIds.map((companyId) => ({
      queryKey: ["companyStatictics", companyId],
      queryFn: () => getComapnyStatictics(companyId),
      enabled: Boolean(companyId) && companyIds.length > 0,
      keepPreviousData: true,
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
    }))
  );
};

export const useGenerateCompanyInvoices = (companyIds) => {
  return useQueries(
    companyIds.map((companyId) => ({
      queryKey: ["companyInvoices", companyId],
      queryFn: () => generateCompanyInvoices(companyId),
      enabled: Boolean(companyId) && companyIds.length > 0,
      keepPreviousData: true,
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
    }))
  );
};

export const useCompanySendingStatus = (companyId) => {
  return useQuery(
    ["companySendingStatus", companyId],
    () => getSendingStatus(companyId),
    {
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
};

export const useCompanySendingStatuses = (companyIds) => {
  return useQueries(
    companyIds.map((companyId) => ({
      queryKey: ["companySendingStatus", companyId],
      queryFn: () => getSendingStatus(companyId),
      enabled: Boolean(companyId) && companyIds.length > 0,
      keepPreviousData: true,
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
      refetchOnWindowFocus: false,
      retry: 1,
    }))
  );
};

export const useCreateHomePageDetails = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(createHomePageDetails, {
    onSuccess: () => {
      enqueueSnackbar("Your web page has been created", {
        variant: "success",
      });
      queryClient.invalidateQueries("companyInfo");
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || "";
      if (message === "You do not have permission to execute this action") {
        enqueueSnackbar("You do not have permission to execute this action", {
          variant: "error",
        });
      }
    },
  });
};

export const useUpdateHomePageDetails = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(updateHomePageDetails, {
    onSuccess: () => {
      enqueueSnackbar("Your web page has been updated", {
        variant: "success",
      });
      queryClient.invalidateQueries("companyInfo");
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || "";
      if (message === "You do not have permission to execute this action") {
        enqueueSnackbar("You do not have permission to execute this action", {
          variant: "error",
        });
      }
    },
  });
};

export const useExtractCompanyDomain = () => {
  return useQuery(["extractCompanyDomain"], () => extractCompanyDomain(), {
    enabled: true,
  });
};

export const useCompanyInfo = () => {
  const { data: domainInfo } = useExtractCompanyDomain() as UseQueryResult<
    any,
    unknown
  >;
  return useQuery(
    ["companyInfo", domainInfo?.companyDomain],
    () =>
      getCompanyInfo({
        params: { domain: domainInfo?.companyDomain },
      }),
    {
      onSuccess: (data: any) => {
        if (data?.id) {
          setCompanyIdCookie(data.id);
        }
        applyTenantTheme({
          primaryColor: data?.theme?.primaryColor,
          secondaryColor: data?.theme?.secondaryColor,
        });
        if ((data?.theme?.iconUrl || data?.theme?.iconDarkUrl) && isBrowser()) {
          const isDarkMode = window.matchMedia(
            "(prefers-color-scheme: dark)"
          ).matches;
          const iconUrl = !isDarkMode
            ? data?.theme?.iconDarkUrl || data?.theme?.iconUrl
            : data?.theme?.iconUrl || data?.theme?.iconDarkUrl;

          changeFavicon(iconUrl);
        }
      },
      onError: (error: any) => {
        if (error.status === 404) {
          navigate("/page-error");
        }
      },
      retry: (failureCount: number, error: any): boolean => {
        const errorResponse = error?.response;
        if (errorResponse?.status === 403 || errorResponse?.status === 404) {
          return false;
        }
        if (failureCount > 1) {
          return false;
        }
        return true;
      },
      refetchOnWindowFocus: false,
      enabled: !!domainInfo?.companyDomain,
    }
  );
};

export const useUpdateCompanyDuringSignup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, params }: { id: string; params: any }) =>
      updateCompanyDuringSignup({ id, params }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("companyInfo");
      },
    }
  );
};

export const useCompanyConnectiveDetails = (domain = null) => {
  const companyDomain = domain != null ? domain : getcompanyDomain();
  const { data: companyConnectiveDetails } = useQuery(
    ["companyConnectiveDetails", companyDomain],
    () =>
      getCompanyConnectiveDetails({
        params: { domain: companyDomain },
      }),
    {
      enabled: !!companyDomain,
      retry: 1,
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
    }
  );
  return companyConnectiveDetails;
};

export const useCompanyTheme = () => {
  const { data: companyInfo } = useCompanyInfo();
  return companyInfo?.theme;
};
export const useCompanyHomePageDetails = () => {
  const { data: companyInfo } = useCompanyInfo();
  return companyInfo?.homepageDetails;
};

export const useCompanyLogo = () => {
  const companyTheme = useCompanyTheme();
  const companyLightLogo = companyTheme?.logoUrl || companyTheme?.iconURL;
  const companyDarkLogo =
    companyTheme?.logoDarkUrl || companyTheme?.iconDarkUrl;
  return {
    lightLogo: companyLightLogo,
    darkLogo: companyDarkLogo,
  };
};

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(updateCompany, {
    onSuccess: () => {
      queryClient.invalidateQueries("companyTheme");
      queryClient.invalidateQueries("companyInfo");
      queryClient.invalidateQueries("companyConnectiveDetails");
      enqueueSnackbar("Company details has been updated", {
        variant: "success",
      });
    },
  });
};

export const useCompanyApproval = () => {
  const queryClient = useQueryClient();
  return useMutation(companyApproval, {
    onSuccess: () => {
      queryClient.invalidateQueries("companyList");
    },
  });
};

export const useToggleCompanyStatus = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(toggleCompanyStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries("companyList");
      enqueueSnackbar("Company status updated successfully", {
        variant: "success",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Error updating company status";
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    },
  });
};

export const useIsAncestorCompanyAdmin = () => {
  const { data: userInfo } = useMyInfo() as UseQueryResult<any, unknown>;
  const { data: staffInfo } = useMyStaffInfo() as UseQueryResult<any, unknown>;
  const { data: companyInfo } = useCompanyInfo() as UseQueryResult<
    any,
    unknown
  >;
  const isCompanyAdminOfOtherCompany = useMemo(() => {
    return staffInfo?.staffRoles.some((role) => {
      const isAdminLikeRole = role.role === STAFF_ROLE.COMPANY_ADMIN;
      return isAdminLikeRole && role.companyId !== companyInfo?.id;
    });
  }, [staffInfo, companyInfo]);
  return userInfo?.isAllowToAccessBackoffice && isCompanyAdminOfOtherCompany;
};

export const useIsOnCompanyAdminBehalf = () => {
  const { data: userInfo } = useMyInfo() as UseQueryResult<any, unknown>;
  const { data: staffInfo } = useMyStaffInfo() as UseQueryResult<any, unknown>;
  const { data: companyInfo } = useCompanyInfo() as UseQueryResult<
    any,
    unknown
  >;
  const isAncestorCompanyAdmin = useIsAncestorCompanyAdmin();

  const currentDomain = useMemo(() => {
    return companyInfo?.domain;
  }, [companyInfo]);

  const userRole = useMemo(() => {
    return userInfo?.userRole;
  }, [userInfo]);

  const isCompanyAdmin = useMemo(() => {
    return staffInfo?.staffRoles.some((role) => {
      const isAdminLikeRole = role.role === STAFF_ROLE.COMPANY_ADMIN;
      return isAdminLikeRole && role.companyId === companyInfo?.id;
    });
  }, [staffInfo, companyInfo]);

  return (
    currentDomain &&
    (userRole === USER_ROLE.ADMIN || isCompanyAdmin || isAncestorCompanyAdmin)
  );
};

export const useInfiniteCompanyList = (
  query = {},
  limit = 10,
  isGetFullData = false
) => {
  const { data: userInfo } = useMyInfo() as UseQueryResult<any, unknown>;
  const { data: staffInfo } = useMyStaffInfo() as UseQueryResult<any, unknown>;

  const queryResult = useInfiniteQuery(
    [QUERY_KEY.INFINITE_COMPANY_LIST, query, limit],
    ({ pageParam = 1 }) =>
      getCompanyList({
        ...query,
        page: pageParam,
        limit,
      }),
    {
      enabled:
        userInfo?.userRole === USER_ROLE.ADMIN ||
        staffInfo?.staffRoles.some(
          (role) =>
            role.role === STAFF_ROLE.COMPANY_ADMIN ||
            role.role === STAFF_ROLE.SUPPORT
        ),
      getNextPageParam: (lastPage: any, allPages: any) => {
        // Check if there are more pages available
        const currentPageNumber = allPages.length;
        const totalPages = Math.ceil(lastPage.total / limit);
        return currentPageNumber < totalPages
          ? currentPageNumber + 1
          : undefined;
      },
    }
  );

  // Auto-fetch next pages if total > currently loaded
  useEffect(() => {
    if (isGetFullData) {
      const pages = queryResult.data?.pages ?? [];
      if (!pages.length) return;
      const last = pages[pages.length - 1];
      const total = Number(last?.total || 0);
      // Try common response shapes: data, items, results
      const loaded = pages.reduce((acc: number, p: any) => {
        const list = p?.data ?? p?.items ?? p?.results ?? [];
        return acc + (Array.isArray(list) ? list.length : 0);
      }, 0);

      if (
        total > 0 &&
        loaded < total &&
        queryResult.hasNextPage &&
        !queryResult.isFetchingNextPage
      ) {
        queryResult.fetchNextPage();
      }
    }
  }, [
    queryResult.data,
    queryResult.hasNextPage,
    queryResult.isFetchingNextPage,
    isGetFullData,
  ]);

  return queryResult;
};
